import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import type {
  NegotiationFeedResponse,
  NegotiationThreadResponse,
  StartInvoiceNegotiationResponse,
} from '../../types/invoice/negotiation';
import type { GetNotificationsResponse } from '../useGetNotifications';

const FEED_PAGE_SIZE = 50;

export const useNegotiationFeed = (params: { search?: string } = {}) => {
  const sessionContext = useContext(SessionContext);
  const sessionId = sessionContext?.sessionId ?? '';

  return useInfiniteQuery({
    queryKey: ['/invoice/negotiations/feed', params.search],
    queryFn: async ({ pageParam }) => {
      const { data } = await axios.post<NegotiationFeedResponse>(
        `${API_URL}/invoice/negotiations/feed`,
        {
          search: params.search || undefined,
          offset: pageParam,
          limit: FEED_PAGE_SIZE,
        },
        { headers: { Authorization: sessionId } },
      );
      return data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.length * FEED_PAGE_SIZE : undefined,
    enabled: !!sessionId,
    refetchInterval: params.search ? undefined : 10_000,
  });
};

export const useNegotiationUnreadCount = (enabled = true) => {
  const sessionContext = useContext(SessionContext);
  const sessionId = sessionContext?.sessionId ?? '';

  return useQuery({
    queryKey: ['/invoice/negotiations/unread-count'],
    queryFn: async () => {
      const { data } = await axios.post<{ count: number }>(
        `${API_URL}/invoice/negotiations/unread-count`,
        {},
        { headers: { Authorization: sessionId } },
      );
      return data;
    },
    enabled: !!sessionId && enabled,
    refetchInterval: 15_000,
  });
};

export const useNegotiationThread = (negotiationId: string | null) => {
  const sessionContext = useContext(SessionContext);
  const sessionId = sessionContext?.sessionId ?? '';

  return useQuery({
    queryKey: ['/invoice/negotiation/thread', negotiationId],
    queryFn: async () => {
      const { data } = await axios.post<NegotiationThreadResponse>(
        `${API_URL}/invoice/negotiation/thread`,
        { negotiationId },
        { headers: { Authorization: sessionId } },
      );
      return data;
    },
    enabled: !!sessionId && !!negotiationId,
    refetchInterval: (query) =>
      query.state.data?.status === 'processing' ? 3_000 : 10_000,
  });
};

export const useMarkNegotiationRead = () => {
  const sessionContext = useContext(SessionContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (negotiationId: string) => {
      await axios.post(
        `${API_URL}/invoice/negotiation/read`,
        { negotiationId },
        { headers: { Authorization: sessionContext?.sessionId ?? '' } },
      );
    },
    onMutate: async (negotiationId) => {
      await queryClient.cancelQueries({ queryKey: ['/notifications'] });
      await queryClient.cancelQueries({ queryKey: ['/invoice/negotiations/unread-count'] });
      await queryClient.cancelQueries({ queryKey: ['/invoice/negotiations/feed'] });

      const previousNotifications = queryClient.getQueriesData<GetNotificationsResponse>({
        queryKey: ['/notifications'],
      });
      const previousUnreadCount = queryClient.getQueryData<{ count: number }>([
        '/invoice/negotiations/unread-count',
      ]);
      const previousFeed = queryClient.getQueriesData<{ pages: NegotiationFeedResponse[] }>({
        queryKey: ['/invoice/negotiations/feed'],
      });

      let clearedNotifications = 0;
      queryClient.setQueriesData<GetNotificationsResponse>(
        { queryKey: ['/notifications'] },
        (old) => {
          if (!old) return old;
          let cleared = 0;
          const notifications = old.notifications.map((notification) => {
            if (
              notification.type === 'negotiation-handoff' &&
              notification.data.type === 'negotiation' &&
              notification.data.negotiationId === negotiationId &&
              !notification.read
            ) {
              cleared += 1;
              return { ...notification, read: true };
            }
            return notification;
          });
          clearedNotifications = Math.max(clearedNotifications, cleared);
          return {
            ...old,
            notifications,
            unreadCount: Math.max(0, old.unreadCount - cleared),
          };
        },
      );

      let wasUnreadInFeed = false;
      queryClient.setQueriesData<{ pages: NegotiationFeedResponse[] }>(
        { queryKey: ['/invoice/negotiations/feed'] },
        (old) => {
          if (!old?.pages) return old;
          let touched = false;
          const pages = old.pages.map((page) => ({
            ...page,
            negotiations: page.negotiations.map((row) => {
              if (row.negotiationId !== negotiationId || !row.unread) return row;
              wasUnreadInFeed = true;
              touched = true;
              return { ...row, unread: false };
            }),
          }));
          return touched ? { ...old, pages } : old;
        },
      );

      if (clearedNotifications > 0 || wasUnreadInFeed) {
        queryClient.setQueryData<{ count: number }>(
          ['/invoice/negotiations/unread-count'],
          (old) => (old ? { count: Math.max(0, old.count - 1) } : old),
        );
      }

      return { previousNotifications, previousUnreadCount, previousFeed };
    },
    onError: (_err, _negotiationId, context) => {
      if (!context) return;
      for (const [key, data] of context.previousNotifications) {
        queryClient.setQueryData(key, data);
      }
      queryClient.setQueryData(
        ['/invoice/negotiations/unread-count'],
        context.previousUnreadCount,
      );
      for (const [key, data] of context.previousFeed) {
        queryClient.setQueryData(key, data);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['/invoice/negotiations/feed'] });
      void queryClient.invalidateQueries({ queryKey: ['/invoice/negotiations/unread-count'] });
      void queryClient.invalidateQueries({ queryKey: ['/notifications'] });
    },
  });
};

export const useMarkAllNegotiationsRead = () => {
  const sessionContext = useContext(SessionContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await axios.post(
        `${API_URL}/invoice/negotiation/read-all`,
        {},
        { headers: { Authorization: sessionContext?.sessionId ?? '' } },
      );
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['/notifications'] });
      await queryClient.cancelQueries({ queryKey: ['/invoice/negotiations/unread-count'] });
      await queryClient.cancelQueries({ queryKey: ['/invoice/negotiations/feed'] });

      const previousNotifications = queryClient.getQueriesData<GetNotificationsResponse>({
        queryKey: ['/notifications'],
      });
      const previousUnreadCount = queryClient.getQueryData<{ count: number }>([
        '/invoice/negotiations/unread-count',
      ]);
      const previousFeed = queryClient.getQueriesData<{ pages: NegotiationFeedResponse[] }>({
        queryKey: ['/invoice/negotiations/feed'],
      });

      queryClient.setQueriesData<GetNotificationsResponse>(
        { queryKey: ['/notifications'] },
        (old) => {
          if (!old) return old;
          let cleared = 0;
          const notifications = old.notifications.map((notification) => {
            if (notification.type === 'negotiation-handoff' && !notification.read) {
              cleared += 1;
              return { ...notification, read: true };
            }
            return notification;
          });
          return {
            ...old,
            notifications,
            unreadCount: Math.max(0, old.unreadCount - cleared),
          };
        },
      );

      queryClient.setQueriesData<{ pages: NegotiationFeedResponse[] }>(
        { queryKey: ['/invoice/negotiations/feed'] },
        (old) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              negotiations: page.negotiations.map((row) =>
                row.unread ? { ...row, unread: false } : row,
              ),
            })),
          };
        },
      );

      queryClient.setQueryData<{ count: number }>(
        ['/invoice/negotiations/unread-count'],
        (old) => (old ? { count: 0 } : old),
      );

      return { previousNotifications, previousUnreadCount, previousFeed };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      for (const [key, data] of context.previousNotifications) {
        queryClient.setQueryData(key, data);
      }
      queryClient.setQueryData(
        ['/invoice/negotiations/unread-count'],
        context.previousUnreadCount,
      );
      for (const [key, data] of context.previousFeed) {
        queryClient.setQueryData(key, data);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['/invoice/negotiations/feed'] });
      void queryClient.invalidateQueries({ queryKey: ['/invoice/negotiations/unread-count'] });
      void queryClient.invalidateQueries({ queryKey: ['/notifications'] });
    },
  });
};

export const useNegotiationReply = () => {
  const sessionContext = useContext(SessionContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { negotiationId: string; body: string }) => {
      const { data } = await axios.post(
        `${API_URL}/invoice/negotiation/reply`,
        input,
        { headers: { Authorization: sessionContext?.sessionId ?? '' } },
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['/invoice/negotiation/thread', variables.negotiationId],
      });
      void queryClient.invalidateQueries({ queryKey: ['/invoice/negotiations/feed'] });
    },
  });
};

export const useStartInvoiceNegotiation = () => {
  const sessionContext = useContext(SessionContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const { data } = await axios.post<StartInvoiceNegotiationResponse>(
        `${API_URL}/invoice/negotiation/start`,
        { invoiceId },
        { headers: { Authorization: sessionContext?.sessionId ?? '' } },
      );
      return data;
    },
    onSuccess: (_data, invoiceId) => {
      void queryClient.invalidateQueries({ queryKey: ['/invoice/detail', invoiceId] });
      void queryClient.invalidateQueries({ queryKey: ['/invoice/negotiations/feed'] });
      void queryClient.invalidateQueries({ queryKey: ['/invoice/negotiations/unread-count'] });
    },
  });
};
